import { Add, Edit } from '@mui/icons-material';
import { Box, Button, Chip, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '~/components/IconButton';
import { Tooltip } from '~/components/Tooltip';
import { useStudio, type StudioAction, type StudioItem } from '../../context';
import { StudioSidebarInterior, StudioSidebarTitle } from '../lib';
import { StudioInteractionDialog } from './StudioInteractionDialog';

export interface StudioSettingsInteractionsTabProps {
  selectedItem: StudioItem;
}

export const StudioSettingsInteractionsTab = ({
  selectedItem,
}: StudioSettingsInteractionsTabProps) => {
  const disabled = selectedItem.locked;
  const theme = useTheme();
  const { t } = useTranslation();
  const { updateItems } = useStudio();
  const [open, setOpen] = useState(false);

  const action = selectedItem.__action;

  const save = (next?: StudioAction) => updateItems([{ id: selectedItem.id, __action: next }]);

  return (
    <StudioSidebarInterior>
      <StudioSidebarTitle>
        {t('pages.studio.edit.settings.interactions.on_click')}
      </StudioSidebarTitle>

      {action ? (
        <Box
          sx={{
            alignItems: 'center',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            display: 'flex',
            gap: 1,
            overflow: 'hidden',
            p: 1.5,
          }}
        >
          <Chip label={action.method} size="small" />

          <Tooltip title={action.url}>
            <Typography sx={{ flex: 1, minWidth: 0 }} variant="body1" noWrap>
              {action.url.replace(/^https?:\/\//, '')}
            </Typography>
          </Tooltip>

          <IconButton
            disabled={disabled}
            label={t('pages.studio.edit.settings.interactions.edit_tooltip')}
            onClick={() => setOpen(true)}
            size="small"
          >
            <Edit fontSize="small" sx={{ color: disabled ? undefined : 'primary.dark' }} />
          </IconButton>
        </Box>
      ) : (
        <Button
          disabled={disabled}
          onClick={() => setOpen(true)}
          startIcon={<Add />}
          variant="outlined"
        >
          {t('pages.studio.edit.settings.interactions.add')}
        </Button>
      )}

      <StudioInteractionDialog
        action={action}
        onClose={() => setOpen(false)}
        onSave={save}
        open={open}
      />
    </StudioSidebarInterior>
  );
};
